import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ChartContainer, ChartTooltip } from '../../components/ui/chart';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';
import Fancybox from '../../lib/FancyBox';
import LearnMore from '../../components/buttons/LearnMore';

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'forgot concept or formula':
      return 'var(--q3-orange-normal)';
    case "didn't read the qs properly":
      return 'var(--q3-cyan-normal)';
    case "didn't understand the qs":
      return 'var(--q3-cyan-normal)';
    case 'due to time pressure':
      return 'var(--q3-amber-normal)';
    case 'didn’t know the concept':
      return 'var(--q3-purple-normal)';
    case 'calculation error':
      return 'var(--q3-blue-normal)';
    case 'not tagged':
      return 'var(--q3-neutral-light)';
    default:
      return 'var(--q3-neutral-light)';
  }
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="overflow-hidden rounded-lg border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md">
        <div className="flex justify-between items-center gap-x-4">
          <p className="flex-grow text-muted-foreground">{data.name}</p>
          <p className="font-bold">{`${Number(data.value).toFixed(2)}%`}</p>
        </div>
      </div>
    );
  }
  return null;
};

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius - 4}
      outerRadius={outerRadius + 4}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
};

export default function AttemptPatternPieChartCard({ data, expanded, onToggleExpand }) {
  const [pieData, setPieData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('incorrect');
  const [activeIndex, setActiveIndex] = useState(null);
  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  useEffect(() => {
    if (!data || data.length === 0) {
      setPieData([]);
      return;
    }

    const percentageKey = selectedFilter === 'incorrect' ? 'incorrectPercentage' : 'unattemptedPercentage';

    const taggedItems = data
      .filter((item) => item.tagTitle !== 'Not Tagged' && item[percentageKey] && parseFloat(item[percentageKey]) > 0)
      .map((item) => ({
        ...item,
        name: item.tagTitle,
        value: parseFloat(item[percentageKey]),
      }));

    const totalTaggedPercentage = taggedItems.reduce((sum, item) => sum + item.value, 0);

    const notTaggedPercentage = Math.max(0, 100 - totalTaggedPercentage);

    const notTaggedTemplate = data.find((item) => item.tagTitle === 'Not Tagged') || {
      tagTitle: 'Not Tagged',
      icon: 'https://cdn.quizrr.in/web-assets/icons/wrong-question-tags/not-tagged.svg',
    };

    if (notTaggedPercentage > 0) {
      taggedItems.push({
        ...notTaggedTemplate,
        name: 'Not Tagged',
        value: notTaggedPercentage,
      });
    }

    setPieData(taggedItems);
  }, [data, selectedFilter]);

  const notTaggedItem = pieData.find((item) => item.name === 'Not Tagged');
  const hasNotTaggedItems = notTaggedItem && notTaggedItem.value > 0;

  return (
    <Card className="mb-6 py-3">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex flex-row flex-wrap items-center gap-2">
          <CardTitle className="text-lg text-[var(--q3-neutral-default)]">
            Your Overview of Attempting Pattern
          </CardTitle>
          <Fancybox>
            <a
              href={
                selectedFilter === 'incorrect'
                  ? 'https://www.youtube.com/embed/4XWfUnxLXEs'
                  : 'https://www.youtube.com/embed/4XWfUnxLXEs'
              }
              data-fancybox
              data-type="iframe"
              className="relative block w-fit overflow-hidden"
            >
              <LearnMore />
            </a>
          </Fancybox>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleExpand}
          className="text-[var(--q3-accent-normal)] border rounded-full"
        >
          {expanded ? 'HIDE' : 'VIEW'}
          {expanded ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
        </Button>
      </CardHeader>

      {expanded && (
        <CardContent className="flex flex-col px-6 pb-0">
          <div>
            <div className="mb-4">
              <Select value={selectedFilter} onValueChange={setSelectedFilter}>
                <SelectTrigger className="w-auto rounded-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="incorrect">Incorrect Qs</SelectItem>
                  <SelectItem value="unattempted">Unattempted Qs</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-8">
              <div className="w-full md:w-52 h-52">
                <ChartContainer config={{ value: { label: 'Value' } }} className="h-full w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart onMouseLeave={onPieLeave}>
                      <Pie
                        activeIndex={activeIndex}
                        activeShape={renderActiveShape}
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={75}
                        paddingAngle={2}
                        dataKey="value"
                        onMouseEnter={onPieEnter}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getStatusColor(entry.name.toLowerCase())} />
                        ))}
                      </Pie>
                      <ChartTooltip cursor={false} content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>

              <div className="flex flex-wrap justify-center md:justify-start gap-4">
                {pieData.map((item, index) => (
                  <div key={index} className="flex w-fit items-center gap-3 rounded-lg py-1 px-2 border-2">
                    <img src={item.icon} className="w-6 h-auto" />
                    <span className="text-xs text-[var(--q3-neutral-default)]">
                      {item.name} {'   '}
                      <span className="font-bold text-sm" style={{ color: getStatusColor(item.name.toLowerCase()) }}>
                        ({`${item.value.toFixed(2)}%`})
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {hasNotTaggedItems && (
            <div className="p-3 bg-[var(--q3-danger-light)] rounded-br-xl rounded-bl-xl -mb-6 -mx-6 mt-4">
              <p className="text-xs text-center text-[var(--q3-danger-normal)]">
                You have <strong>not tagged {notTaggedItem?.value?.toFixed(2) || '0.00'}%</strong> of your Qs, please
                tag them in solutions screen to get correct data & analysis
              </p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
